/**
 * Product page pre-purchase theme slot contract (custom slots 1–4).
 * Each slot is bound 1:1 to an upsell layout / theme app block.
 * Shared by editor, preview runtime, and server validation.
 */
import { isStaticProductPageUpsell } from "./product-page-upsell-template.js";
import { PRODUCT_SHELL_LAYOUT } from "./product-shell-layout.js";

/** DOM id prefix for theme app extension blocks: `#weupsell-slot-1`, … */
export const PRODUCT_PAGE_SLOT_ID_PREFIX = "weupsell-slot-";

/**
 * Allowed merchant-facing slot numbers.
 * Slots 1–4 = product page upsell layouts; slot 5 = Volume Discount (separate template).
 */
export const PRODUCT_PAGE_SLOT_NUMBERS = Object.freeze([
  "1",
  "2",
  "3",
  "4",
  "5",
]);

/** Upsell-only slots (excludes Volume Discount). */
export const PRODUCT_PAGE_UPSELL_SLOT_NUMBERS = Object.freeze([
  "1",
  "2",
  "3",
  "4",
]);

/** Volume Discount theme slot. */
export const VOLUME_DISCOUNT_SHELL = "volume-discount-v1";
export const VOLUME_DISCOUNT_SLOT_NO = "5";

/**
 * Layout shell bound to each theme slot.
 * Slot 1 Horizontal · 2 Carousel · 3 Product Add-Ons · 4 FBT · 5 Volume Discount
 */
export const PRODUCT_PAGE_SLOT_LAYOUT_BY_SLOT = Object.freeze({
  "1": PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL,
  "2": PRODUCT_SHELL_LAYOUT.CART_DRAWER_VERTICAL,
  "3": PRODUCT_SHELL_LAYOUT.CART_DRAWER_CHECKBOX_STACK,
  "4": PRODUCT_SHELL_LAYOUT.CART_DRAWER_FBT_BUNDLE,
  "5": VOLUME_DISCOUNT_SHELL,
});

export const PRODUCT_PAGE_SLOT_META = Object.freeze([
  {
    slotNo: "1",
    shell: PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL,
    label: "Horizontal row",
    blockHandle: "prepurchase-slot-1",
    blockName: "WeUpsell Horizontal row",
    description: "Image, details, and add button in one row.",
  },
  {
    slotNo: "2",
    shell: PRODUCT_SHELL_LAYOUT.CART_DRAWER_VERTICAL,
    label: "Carousel cards",
    blockHandle: "prepurchase-slot-2",
    blockName: "WeUpsell Carousel cards",
    description: "Vertical cards in a scrollable row.",
  },
  {
    slotNo: "3",
    shell: PRODUCT_SHELL_LAYOUT.CART_DRAWER_CHECKBOX_STACK,
    label: "Product Add-Ons",
    blockHandle: "prepurchase-slot-3",
    blockName: "WeUpsell Product Add-Ons",
    description: "Add discounted extras that go well with the main item.",
  },
  {
    slotNo: "4",
    shell: PRODUCT_SHELL_LAYOUT.CART_DRAWER_FBT_BUNDLE,
    label: "Frequently Bought Together",
    blockHandle: "prepurchase-slot-4",
    blockName: "WeUpsell Bought Together",
    description: "Bundle products with related items — all discounted together.",
  },
  {
    slotNo: "5",
    shell: VOLUME_DISCOUNT_SHELL,
    label: "Volume Discount",
    blockHandle: "volume-discount",
    blockName: "WeUpsell Volume Discount",
    description: "Quantity breaks or Buy X Get Y on the product page.",
  },
]);

const SHELL_TO_SLOT = Object.freeze(
  Object.fromEntries(
    PRODUCT_PAGE_SLOT_META.map((meta) => [meta.shell, meta.slotNo])
  )
);

/**
 * @param {string | null | undefined} slotNo
 * @returns {boolean}
 */
export function isValidProductPageSlotNo(slotNo) {
  return PRODUCT_PAGE_SLOT_NUMBERS.includes(String(slotNo ?? ""));
}

/**
 * Normalize legacy / alias shells to the product-page upsell shell ids.
 * @param {string | null | undefined} shell
 * @returns {string | null}
 */
export function normalizeProductPageUpsellShell(shell) {
  if (shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL) {
    return PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL;
  }
  if (shell && SHELL_TO_SLOT[shell]) {
    return shell;
  }
  return null;
}

/**
 * @param {string | null | undefined} shell
 * @returns {string | null} slot "1"–"4"
 */
export function getProductPageSlotForShell(shell) {
  const normalized = normalizeProductPageUpsellShell(shell);
  if (!normalized) return null;
  return SHELL_TO_SLOT[normalized] || null;
}

/**
 * @param {string | number | null | undefined} slotNo
 * @returns {string | null}
 */
export function getProductPageShellForSlot(slotNo) {
  const normalized = String(slotNo ?? "").trim();
  return PRODUCT_PAGE_SLOT_LAYOUT_BY_SLOT[normalized] || null;
}

/**
 * @param {string | number | null | undefined} slotNo
 * @returns {(typeof PRODUCT_PAGE_SLOT_META)[number] | null}
 */
export function getProductPageSlotMeta(slotNo) {
  const normalized = String(slotNo ?? "").trim();
  return PRODUCT_PAGE_SLOT_META.find((meta) => meta.slotNo === normalized) || null;
}

/**
 * @param {string | number | null | undefined} slotNo
 * @returns {string}
 */
export function buildProductPageSlotElementId(slotNo) {
  return `${PRODUCT_PAGE_SLOT_ID_PREFIX}${String(slotNo ?? "").trim()}`;
}

/**
 * @param {string | null | undefined} elementId
 * @returns {string | null}
 */
export function extractProductPageSlotNoFromElementId(elementId) {
  const match = String(elementId || "").match(/^weupsell-slot-(\d+)$/);
  return match ? match[1] : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {string | null}
 */
export function extractProductPageSlotNoFromElement(element) {
  if (!element) return null;
  const fromData = element.getAttribute?.("data-weupsell-prepurchase-slot");
  if (fromData && isValidProductPageSlotNo(fromData)) {
    return String(fromData);
  }
  return extractProductPageSlotNoFromElementId(element.id);
}

/**
 * @returns {HTMLElement[]}
 */
export function queryProductPageSlotElements() {
  if (typeof document === "undefined") return [];
  const seen = new WeakSet();
  const nodes = document.querySelectorAll(
    '[data-weupsell-prepurchase-slot], [id^="weupsell-slot-"], [id^="weupsell-prepurchase-slot-"]'
  );
  const result = [];
  nodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (seen.has(node)) return;
    const slotNo = extractProductPageSlotNoFromElement(node);
    if (!slotNo || !isValidProductPageSlotNo(slotNo)) return;
    seen.add(node);
    result.push(node);
  });
  return result;
}

/**
 * @param {{ generalSettings?: { condition?: { embed?: object } } } | null | undefined} popup
 * @returns {object | null}
 */
export function getProductPageEmbedConfig(popup) {
  return popup?.generalSettings?.condition?.embed ?? null;
}

/**
 * Whether this popup mounts via theme slot blocks (not CSS selector embed).
 * @param {{ generalSettings?: { condition?: { embed?: object } } } | null | undefined} popup
 * @returns {boolean}
 */
export function isProductPageSlotMode(popup) {
  const embed = getProductPageEmbedConfig(popup);
  if (!embed) return false;

  // Checkout / thank-you / order-status extensions reuse slot_no but are not product page slots.
  const extensionSurface = String(embed.extension_surface || "").trim();
  if (
    extensionSurface &&
    extensionSurface !== "product_page"
  ) {
    return false;
  }

  if (embed.slot_mode === true) return true;
  if (embed.slot_no != null && isValidProductPageSlotNo(embed.slot_no)) {
    return true;
  }
  return false;
}

/**
 * Resolved slot number stored on the popup (`"1"` | `"2"` | `"3"` | `"4"`).
 * @param {{ generalSettings?: { condition?: { embed?: object } } } | null | undefined} popup
 * @returns {string | null}
 */
export function getProductPageSlotNo(popup) {
  const embed = getProductPageEmbedConfig(popup);
  if (!embed) return null;
  const slotNo = embed.slot_no ?? embed.custom_slot;
  if (slotNo == null) return null;
  const normalized = String(slotNo).trim();
  return isValidProductPageSlotNo(normalized) ? normalized : null;
}

/**
 * Resolve the upsell layout shell for a product page campaign.
 * Prefers PRODUCT block shell, then cartDrawerUpsellShell, then slot mapping.
 * @param {object | null | undefined} popup
 * @returns {string}
 */
export function resolveProductPageUpsellShellFromPopup(popup) {
  const steps = Array.isArray(popup?.schema) ? popup.schema : [];
  for (const step of steps) {
    const sections = Array.isArray(step?.section)
      ? step.section
      : Array.isArray(step?.sections)
        ? step.sections
        : [];
    const productSection = sections.find((section) => section?.type === "PRODUCT");
    const fromProduct = normalizeProductPageUpsellShell(
      productSection?.setting?.layout?.shell
    );
    if (fromProduct) return fromProduct;
  }

  const fromGs = normalizeProductPageUpsellShell(
    popup?.generalSettings?.cartDrawerUpsellShell ||
      popup?.generalSettings?.productShellLayout
  );
  if (fromGs) return fromGs;

  const slotNo = getProductPageSlotNo(popup);
  return getProductPageShellForSlot(slotNo) || PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL;
}

/**
 * @param {object | null | undefined} popup
 * @param {string | number | null | undefined} slotNo
 * @returns {boolean}
 */
export function matchesProductPageSlot(popup, slotNo) {
  if (!popup || slotNo == null) return false;
  if (!isProductPageSlotMode(popup)) return false;
  return getProductPageSlotNo(popup) === String(slotNo);
}

/**
 * @param {object[] | null | undefined} popups
 * @param {string | number | null | undefined} slotNo
 * @returns {object[]}
 */
export function filterPopupsForProductPageSlot(popups, slotNo) {
  if (!Array.isArray(popups) || slotNo == null) return [];
  return popups.filter((popup) => matchesProductPageSlot(popup, slotNo));
}

/**
 * Pick the first active published popup for a theme slot.
 * @param {object[] | null | undefined} popups
 * @param {string | number | null | undefined} slotNo
 * @returns {object | null}
 */
export function resolvePopupForProductPageSlot(popups, slotNo) {
  const matches = filterPopupsForProductPageSlot(popups, slotNo).filter((popup) => {
    if (popup?.is_active === false) return false;
    if (popup?.is_published === false) return false;
    return true;
  });
  return matches[0] ?? null;
}

/**
 * Default embed payload for new product page upsell campaigns (theme slot 1).
 * @param {string} [slotNo]
 * @returns {object}
 */
export function buildDefaultProductPageSlotEmbed(slotNo = "1") {
  const normalized = isValidProductPageSlotNo(slotNo) ? String(slotNo) : "1";
  return {
    is_embedded: true,
    slot_mode: true,
    slot_no: normalized,
    step: 0,
  };
}

function patchProductSectionsShell(popup, shell) {
  if (!shell || !Array.isArray(popup?.schema)) return popup?.schema;
  return popup.schema.map((step) => {
    if (!step || typeof step !== "object") return step;
    const sectionKey = Array.isArray(step.section)
      ? "section"
      : Array.isArray(step.sections)
        ? "sections"
        : null;
    if (!sectionKey) return step;
    const sections = step[sectionKey];
    let changed = false;
    const nextSections = sections.map((section) => {
      if (section?.type !== "PRODUCT") return section;
      changed = true;
      return {
        ...section,
        setting: {
          ...(section.setting || {}),
          layout: {
            ...(section.setting?.layout || {}),
            shell,
          },
        },
      };
    });
    if (!changed) return step;
    return { ...step, [sectionKey]: nextSections };
  });
}

/**
 * Apply theme slot + matching layout shell on a popup payload.
 * @param {object | null | undefined} popup
 * @param {string | number} slotNo
 * @returns {object}
 */
export function withProductPageSlot(popup, slotNo) {
  const embed = buildDefaultProductPageSlotEmbed(slotNo);
  const shell =
    getProductPageShellForSlot(embed.slot_no) ||
    PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL;
  const nextSchema = patchProductSectionsShell(popup, shell);

  return {
    ...(popup || {}),
    ...(nextSchema ? { schema: nextSchema } : {}),
    generalSettings: {
      ...(popup?.generalSettings || {}),
      cartDrawerUpsellShell: shell,
      condition: {
        ...(popup?.generalSettings?.condition || {}),
        embed: {
          ...(popup?.generalSettings?.condition?.embed || {}),
          ...embed,
        },
      },
    },
  };
}

/**
 * Bind popup to the theme slot that matches a layout shell.
 * @param {object | null | undefined} popup
 * @param {string | null | undefined} shell
 * @returns {object}
 */
export function withProductPageLayoutShell(popup, shell) {
  const slotNo =
    getProductPageSlotForShell(shell) || PRODUCT_PAGE_SLOT_NUMBERS[0];
  return withProductPageSlot(popup, slotNo);
}

/**
 * @param {object[] | null | undefined} peers
 * @param {string | null | undefined} excludePopupId
 * @returns {string | null}
 */
export function findNextAvailableProductPageSlot(peers, excludePopupId = null) {
  if (!Array.isArray(peers)) return PRODUCT_PAGE_SLOT_NUMBERS[0];

  const used = new Set();
  peers.forEach((popup) => {
    if (!popup) return;
    if (
      excludePopupId &&
      String(popup._id ?? popup.id ?? "") === String(excludePopupId)
    ) {
      return;
    }
    if (!isProductPageCampaignPeer(popup)) return;
    const slotNo = getProductPageSlotNo(popup);
    if (slotNo) used.add(slotNo);
  });

  for (const slotNo of PRODUCT_PAGE_SLOT_NUMBERS) {
    if (!used.has(slotNo)) return slotNo;
  }

  return null;
}

/**
 * @param {object[] | null | undefined} peers
 * @param {string | null | undefined} excludePopupId
 * @returns {string[]}
 */
export function listOccupiedProductPageSlots(peers, excludePopupId = null) {
  const occupied = [];
  (Array.isArray(peers) ? peers : []).forEach((peer) => {
    if (!peer) return;
    if (
      excludePopupId &&
      String(peer._id ?? peer.id ?? "") === String(excludePopupId)
    ) {
      return;
    }
    if (!isProductPageCampaignPeer(peer)) return;
    const slotNo = getProductPageSlotNo(peer);
    if (slotNo) occupied.push(slotNo);
  });
  return occupied;
}

/**
 * Layouts already chosen by any other product page campaign (draft or live).
 * @param {object[] | null | undefined} peers
 * @param {string | null | undefined} excludePopupId
 * @returns {string[]}
 */
export function listOccupiedProductPageLayouts(peers, excludePopupId = null) {
  const occupied = new Set();
  (Array.isArray(peers) ? peers : []).forEach((peer) => {
    if (!peer) return;
    if (
      excludePopupId &&
      String(peer._id ?? peer.id ?? "") === String(excludePopupId)
    ) {
      return;
    }
    if (!isProductPageCampaignPeer(peer)) return;

    const shell = resolveProductPageUpsellShellFromPopup(peer);
    if (shell) occupied.add(shell);

    const fromSlot = getProductPageShellForSlot(getProductPageSlotNo(peer));
    if (fromSlot) occupied.add(fromSlot);
  });
  return [...occupied];
}

function isProductPageSlotUsedByPeer(peers, slotNo, excludePopupId = null) {
  if (!slotNo) return false;

  return (Array.isArray(peers) ? peers : []).some((peer) => {
    if (!peer) return false;
    if (
      excludePopupId &&
      String(peer._id ?? peer.id ?? "") === String(excludePopupId)
    ) {
      return false;
    }
    if (!isProductPageCampaignPeer(peer)) return false;
    return getProductPageSlotNo(peer) === String(slotNo);
  });
}

/**
 * Pick an available layout slot for a product page upsell.
 * Prefer the slot that matches the campaign layout; reassign when taken.
 * @param {object} popup
 * @param {object[] | null | undefined} peers
 * @param {string | null | undefined} excludePopupId
 * @returns {object}
 */
export function resolveProductPageSlotAssignment(
  popup,
  peers,
  excludePopupId = null
) {
  let nextPopup = popup;
  const layoutShell = resolveProductPageUpsellShellFromPopup(nextPopup);
  const layoutSlot = getProductPageSlotForShell(layoutShell);
  let requestedSlot = getProductPageSlotNo(nextPopup) || layoutSlot;

  if (
    layoutSlot &&
    !isProductPageSlotUsedByPeer(peers, layoutSlot, excludePopupId)
  ) {
    requestedSlot = layoutSlot;
  }

  if (
    !requestedSlot ||
    isProductPageSlotUsedByPeer(peers, requestedSlot, excludePopupId)
  ) {
    const availableSlot = findNextAvailableProductPageSlot(peers, excludePopupId);
    if (availableSlot) {
      nextPopup = withProductPageSlot(nextPopup, availableSlot);
      requestedSlot = availableSlot;
    }
  } else {
    nextPopup = withProductPageSlot(nextPopup, requestedSlot);
  }

  if (!isProductPageSlotMode(nextPopup)) {
    nextPopup = withProductPageSlot(nextPopup, requestedSlot || "1");
  }

  return nextPopup;
}

function isProductPageCampaignPeer(popup) {
  if (!popup) return false;
  if (isStaticProductPageUpsell(popup)) return true;
  if (popup?.generalSettings?.isStaticVolumeDiscount === true) return true;
  if (isProductPageSlotMode(popup)) return true;
  const type = String(popup.popUpType || popup.template_type || "")
    .trim()
    .toLowerCase();
  return type === "product page upsell" || type === "volume discount";
}

function listProductPageCampaignPeers(peers, excludePopupId = null) {
  return (Array.isArray(peers) ? peers : []).filter((peer) => {
    if (!peer) return false;
    if (
      excludePopupId &&
      String(peer._id ?? peer.id ?? "") === String(excludePopupId)
    ) {
      return false;
    }
    return isProductPageCampaignPeer(peer);
  });
}

function isActiveProductPageSlotPopup(popup) {
  if (!popup || popup.is_active !== true) return false;
  return isProductPageCampaignPeer(popup);
}

/**
 * @param {object} popup
 * @param {{ peers?: object[], excludePopupId?: string | null, requireSlot?: boolean }} [options]
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateProductPageSlotConstraints(
  popup,
  {
    peers = [],
    excludePopupId = null,
    requireSlot = true,
    publishing = false,
    isNewCampaign = false,
  } = {}
) {
  const errors = [];

  if (!popup || !isProductPageCampaignPeer(popup)) {
    return { ok: true, errors };
  }

  const slotNo = getProductPageSlotNo(popup);
  const layoutShell = resolveProductPageUpsellShellFromPopup(popup);
  const layoutSlot = getProductPageSlotForShell(layoutShell);
  const layoutMeta = getProductPageSlotMeta(layoutSlot || slotNo);

  if (requireSlot && !slotNo) {
    errors.push(
      "Product page upsell requires a theme layout block (Horizontal, Carousel, Product Add-Ons, or Frequently Bought Together)."
    );
  }

  if (slotNo && !isValidProductPageSlotNo(slotNo)) {
    errors.push(
      `Invalid product page slot "${slotNo}". Use layout slots 1–5.`
    );
  }

  if (slotNo && layoutSlot && slotNo !== layoutSlot) {
    errors.push(
      `Layout "${layoutMeta?.label || layoutShell}" must use the matching theme block (${getProductPageSlotMeta(layoutSlot)?.blockName || `slot ${layoutSlot}`}).`
    );
  }

  const allPeers = listProductPageCampaignPeers(peers, excludePopupId);
  const activePeers = allPeers.filter((peer) => isActiveProductPageSlotPopup(peer));
  const countsAsActiveCampaign = popup.is_active === true || publishing;

  if (isNewCampaign && allPeers.length >= PRODUCT_PAGE_SLOT_NUMBERS.length) {
    errors.push(
      `Maximum ${PRODUCT_PAGE_SLOT_NUMBERS.length} product page upsell campaigns allowed. Delete an existing campaign to create a new one.`
    );
  }

  // One layout / theme block per campaign — drafts and live alike.
  if (slotNo) {
    const duplicateSlot = allPeers.find(
      (peer) => getProductPageSlotNo(peer) === slotNo
    );
    if (duplicateSlot) {
      const meta = getProductPageSlotMeta(slotNo);
      errors.push(
        `${meta?.label || `Layout slot ${slotNo}`} is already used by another product page upsell. Change or delete that campaign first.`
      );
    }
  }

  if (layoutShell) {
    const duplicateLayout = allPeers.find((peer) => {
      const peerShell = resolveProductPageUpsellShellFromPopup(peer);
      const peerSlotShell = getProductPageShellForSlot(getProductPageSlotNo(peer));
      return peerShell === layoutShell || peerSlotShell === layoutShell;
    });
    if (duplicateLayout) {
      errors.push(
        `${layoutMeta?.label || "This layout"} is already used by another product page upsell. Change or delete that campaign first.`
      );
    }
  }

  if (countsAsActiveCampaign) {
    if (activePeers.length >= PRODUCT_PAGE_SLOT_NUMBERS.length) {
      errors.push(
        `Maximum ${PRODUCT_PAGE_SLOT_NUMBERS.length} active product page upsell campaigns allowed.`
      );
    }
  }

  return { ok: errors.length === 0, errors };
}
